import React, { useEffect, useState } from "react";
import { Grid3X3, Play, Square } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const SHORT = { Birth: "B", Death: "D", "Global Event": "G", "Song Release": "S", "Film Release": "F", Invention: "I", Discovery: "Dc" };

export const CoveragePanel = ({ onPickDate, refreshKey }) => {
  const [cov, setCov] = useState(null);
  const [days, setDays] = useState(60);
  const [job, setJob] = useState(null);

  useEffect(() => { api.get("/admin/events/coverage", { params: { days } }).then((r) => setCov(r.data)).catch(() => setCov(null)); }, [days, refreshKey, job?.done]);
  useEffect(() => {
    let t;
    const poll = async () => {
      try { const r = await api.get("/admin/events/suggest/bulk/status"); setJob(r.data); if (["starting", "running"].includes(r.data.status)) t = setTimeout(poll, 4000); } catch { /* ignore */ }
    };
    poll();
    return () => clearTimeout(t);
  }, []);

  const startBulk = async () => {
    if (!window.confirm(`Ask the AI to draft events for every date in the next ${days} days that has fewer than ${cov?.target ?? 12} events? Drafts stay hidden until approved.`)) return;
    try { await api.post("/admin/events/suggest/bulk", { target: cov?.target ?? 12, horizon_days: days }); toast.success("Bulk AI fill started"); setJob({ status: "starting", done: 0, total: 0 }); setTimeout(() => window.location.reload(), 1500); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start"); }
  };
  const cancelBulk = async () => { await api.post("/admin/events/suggest/bulk/cancel"); toast.info("Cancelling after the current date…"); };
  const running = job && ["starting", "running"].includes(job.status);

  return (
    <section className="mb-8 rounded-2xl border border-slate-200 bg-white p-5" data-testid="coverage-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <h2 className="text-base md:text-lg font-bold text-ink flex items-center gap-2"><Grid3X3 className="w-4 h-4 text-gold" /> Coverage — target {cov?.target ?? 12}/day
          {cov && <span className="font-mono text-xs text-ink-muted font-normal" data-testid="coverage-summary">· {cov.public_total} public in window · short {cov.short_total}</span>}
        </h2>
        <div className="flex items-center gap-2">
          <select data-testid="coverage-days" value={days} onChange={(e) => setDays(+e.target.value)} className="h-9 rounded-full border border-slate-300 bg-white px-3 text-xs font-mono"><option value={30}>Next 30 days</option><option value={60}>Next 60 days</option><option value={90}>Next 90 days</option><option value={366}>Whole year</option></select>
          {running ? (
            <button data-testid="bulk-cancel" onClick={cancelBulk} className="inline-flex items-center gap-2 px-4 h-9 rounded-full border border-red-200 bg-red-50 text-red-700 font-mono text-xs"><Square className="w-3.5 h-3.5" /> Stop ({job.done}/{job.total})</button>
          ) : (
            <button data-testid="bulk-start" onClick={startBulk} className="inline-flex items-center gap-2 px-4 h-9 rounded-full bg-paper border border-gold text-ink font-mono text-xs hover:bg-gold"><Play className="w-3.5 h-3.5" /> Bulk AI fill</button>
          )}
        </div>
      </div>
      {job && job.status !== "idle" && (
        <p className="font-mono text-xs text-ink-muted mb-3" data-testid="bulk-status">Bulk fill {job.status}: {job.done}/{job.total} dates · {job.drafts} drafts · {job.errors} errors{job.last_date ? ` · last ${job.last_date}` : ""}</p>
      )}
      {cov && (
        <>
          <div className="flex flex-wrap gap-3 font-mono text-[11px] text-ink-muted mb-3">
            {cov.categories.map((c) => <span key={c}><strong className="text-ink">{SHORT[c]}</strong> {c}: {cov.totals[c]}</span>)}
          </div>
          <div className="overflow-x-auto">
            <table className="text-[11px] font-mono" data-testid="coverage-table">
              <thead><tr className="text-ink-muted"><th className="px-2 py-1 text-left">Date</th>{cov.categories.map((c) => <th key={c} className="px-2 py-1" title={c}>{SHORT[c]}</th>)}<th className="px-2 py-1">Drafts</th><th className="px-2 py-1">Public</th><th className="px-2 py-1">Short</th></tr></thead>
              <tbody>
                {cov.dates.map((d) => (
                  <tr key={d.date_dd_mm} data-testid={`cov-${d.date_dd_mm}`} onClick={() => onPickDate?.(d.date_dd_mm)} className={`cursor-pointer hover:bg-slate-50 ${d.short === 0 ? "bg-gold/10" : d.public === 0 ? "bg-red-50" : ""}`}>
                    <td className="px-2 py-0.5 text-ink font-semibold">{d.date_dd_mm}</td>
                    {cov.categories.map((c) => <td key={c} className={`px-2 py-0.5 text-center ${d.categories[c] ? "text-ink" : "text-slate-300"}`}>{d.categories[c]}</td>)}
                    <td className="px-2 py-0.5 text-center text-gold">{d.drafts || ""}</td>
                    <td className="px-2 py-0.5 text-center text-ink">{d.public}</td>
                    <td className={`px-2 py-0.5 text-center font-semibold ${d.short ? "text-red-600" : "text-ink-muted"}`}>{d.short || "✓"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
};
